import { useCallback, useEffect, useRef } from 'react';

/**
 * Debounce a "commit" side-effect (a PATCH) keyed by an identifier.
 *
 * Every distinct `key` gets its own pending timer; scheduling a new value for a
 * key replaces (clears) that key's previous timer so a stale payload can never
 * fire after a newer edit (debounce + clear race rule). All timers are cleared
 * on unmount, and `cancel(key)` / `flush(key)` are exposed for explicit
 * clear/blur paths.
 *
 * `commit` is read from a ref so the returned callbacks stay stable and the
 * latest closure (with fresh deps) always runs.
 */
export function useDebouncedCommit<T>(
  commit: (key: string, value: T) => void,
  delayMs: number
) {
  const commitRef = useRef(commit);
  commitRef.current = commit;

  // Per-key pending timer + the value queued for that timer.
  const pending = useRef<Map<string, { timer: ReturnType<typeof setTimeout>; value: T }>>(
    new Map()
  );

  const cancel = useCallback((key: string) => {
    const entry = pending.current.get(key);
    if (entry) {
      clearTimeout(entry.timer);
      pending.current.delete(key);
    }
  }, []);

  const schedule = useCallback(
    (key: string, value: T) => {
      // Clear the previous pending fire for this key so the older payload can
      // never overwrite the newer one.
      cancel(key);
      const timer = setTimeout(() => {
        pending.current.delete(key);
        commitRef.current(key, value);
      }, delayMs);
      pending.current.set(key, { timer, value });
    },
    [cancel, delayMs]
  );

  const flush = useCallback(
    (key: string) => {
      const entry = pending.current.get(key);
      if (entry) {
        clearTimeout(entry.timer);
        pending.current.delete(key);
        commitRef.current(key, entry.value);
      }
    },
    []
  );

  // Clear every pending timer on unmount — no stale PATCH after teardown.
  useEffect(() => {
    const map = pending.current;
    return () => {
      map.forEach((entry) => clearTimeout(entry.timer));
      map.clear();
    };
  }, []);

  return { schedule, cancel, flush };
}
